/**
 * Demo Mode Configuration
 * Controls demo-specific behavior and compliance features
 */

export const DEMO_MODE = {
  // Enable demo mode (set via environment variable)
  enabled: import.meta.env.VITE_DEMO_MODE === 'true',

  // Data retention policy
  dataRetentionHours: 24,

  // Demo-specific messages
  messages: {
    banner: '🚀 Agos Demo Mode – Not for Production Use',
    bannerSubtitle: 'This is a demonstration environment. NPC registration is pending. Your data will be deleted within 24 hours.',
    consentTitle: 'Demo Mode Consent',
    consentDescription: 'Please read before proceeding',
  },

  // Compliance features
  compliance: {
    // Show demo banner on all pages
    showBanner: true,

    // Require consent before verification
    requireConsent: true,

    // Show privacy notice
    showPrivacyNotice: true,

    // Enable data minimization
    minimizeDataCollection: true,

    // Enable 24-hour purge
    enableAutoPurge: true,
  },

  // Logging for demo purposes
  logging: {
    // Log all verification attempts
    logVerifications: true,

    // Log data access
    logDataAccess: true,

    // Log consent acceptance
    logConsent: true,
  },
};

/**
 * Check if demo mode is enabled
 */
export function isDemoMode(): boolean {
  return DEMO_MODE.enabled;
}

/**
 * Get demo mode status for display
 */
export function getDemoModeStatus() {
  return {
    isDemoMode: isDemoMode(),
    dataRetentionHours: DEMO_MODE.dataRetentionHours,
    requiresConsent: DEMO_MODE.compliance.requireConsent,
    showBanner: DEMO_MODE.compliance.showBanner,
  };
}

/**
 * Log demo-specific events
 */
export function logDemoEvent(eventType: string, data: any) {
  if (!isDemoMode()) return;

  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    eventType,
    data,
    demoMode: true,
  };

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log('[DEMO MODE]', logEntry);
  }

  // Could also send to analytics or logging service
  // await sendToLoggingService(logEntry);
}

/**
 * Get demo mode disclaimer for display
 */
export function getDemoDisclaimer(): string {
  return `This is a demonstration environment. NPC registration is pending. Your data will be deleted within 24 hours. By proceeding, you consent to temporary processing for demo purposes.`;
}
